// Contents of this file were produced with the help of a code generation tool and
// subsequently reviewed and edited by a human.
//
// While some of the code was created by an AI-assisted service, manual adjustments
// have been made to ensure correctness, readability, functionality and compliance
// to Avalara's coding standards and guidelines.
// Any future modifications should be approached with caution to avoid
// overwriting the manual changes.
//
//
// This source code is owned by Avalara Inc. Unauthorized copying of this file,
// via any medium, is strictly prohibited. Proprietary and confidential.
//
// For more information, refer to the documentatin or contact the development team.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import _, { kebabCase } from 'lodash';
import { FormikValues, useFormik } from 'formik';
import FieldInput from '../../../components/FieldInput/FieldInput';
import FieldDate from '../../../components/FieldDate/FieldDate';
import ToastMessage from '../../../components/ToastMessage/ToastMessage';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import validateEmpty from '../../../utils/validateEmpty';
import { getTaxBill, getErrorMessage, updateTaxBill, createTaxBill, getAssessmentNotice } from '../../../services';
import {
  AssessmentNotice,
  IErrorBase,
  IToastMessage,
} from '../../../interface';
import style from './TaxBillSetup.module.scss';
import { AxiosResponse } from 'axios';
import {
  getFirstDateOfThisYear,
  getLastDateOfThisYear,
} from '../../../utils/date';
import Loader, { LoaderIndicator } from '../../../components/Loader/Loader';
import DiscardChangeConfirmModal from '../../../components/DiscardChangeConfirmModal/DiscardChangeConfirmModal';
import TooltipWrapper from '../../../components/TooltipWrapper/TooltipWrapper';
import { TaxBillStatus, AssessmentNoticeStatus, TOOLTIP_SECTIONS, NoticesBillsTab } from '../../../enums';
import { currencyToNumber, formatCurrency } from '../../../utils/formatNumber';
import FieldTextarea from '../../../components/FieldTextarea/FieldTextarea';
import Divider from '../../../components/Divider/Divider';
import CustomCheckbox from '../../../components/CustomCheckbox/CustomCheckbox';

// Init formik values
const newInit = {
  id: null,
  assessmentNoticeId: null,
  assessmentNotice: {} as AssessmentNotice,
  taxBillStatus: TaxBillStatus.Missing,
  collector: '',
  collectorAccountNumber: '',
  taxableValue: null,
  totalTaxAmount: null,
  paymentDeadline: null,
  paid: false,
  datePaid: null,
  paymentDetails: '',
};

const requiredField = [
  'paymentDeadline'
];

function TaxBillSetup() {
  const navigate = useNavigate();
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const assessmentNoticeId = searchParams.get('assessmentNoticeId');
  
  const [navBlock, setNavBlock] = useState<boolean>();

  const [fetchingTaxBill, setFetchingTaxBill] = useState(false);
  const [fetchingAssessmentNotice, setFetchingAssessmentNotice] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleResponse = (promise: Promise<AxiosResponse>) => {
    return promise
      .then((e: AxiosResponse<IErrorBase>) => {
        if (_.inRange(e.status, 200, 299)) {
          navigate(`/taxes?tab=${NoticesBillsTab.TaxBills}`);
        }
      })
      .catch((e) => {
        setToastMessage({
          visible: true,
          message: getErrorMessage(e),
          type: "error",
        });
      })
      .finally(() => setSubmitting(false));
  }

  const setFinalStatus = () => {
    const taxableValue = currencyToNumber(formik.values.taxableValue || 0);
    const totalTaxAmount = currencyToNumber(formik.values.totalTaxAmount || 0);
    const hasValidValues = taxableValue > 0 && totalTaxAmount > 0;

    if (formik.values.paid) {
      formik.setFieldValue('taxBillStatus', TaxBillStatus.Paid);
    } else if (hasValidValues) {
      formik.setFieldValue('taxBillStatus', TaxBillStatus.Received);
    } else {
      formik.setFieldValue('taxBillStatus', TaxBillStatus.Missing);
    }
  };

  // Flag for submit or not
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [submitToast, setSubmitToast] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState(newInit);
  const formik = useFormik<FormikValues>({
    initialValues: initialValues,
    enableReinitialize: true,
    validate: (values) => {
      // Validate form
      if (isSubmit) {
        const _required = _.cloneDeep(requiredField);
        if (values.paid) {
          _required.push('datePaid');
        }
        const errors = _.defaults(
          validateEmpty(_.pick(values, _required)),
        );
        const result = _.every(_.values(errors), (e: string | []) => {
          return _.every(e, _.isEmpty);
        });

        if (!result) {
          if (submitToast) {
            setSubmitToast(false);
            setToastMessage({
              visible: true,
              message:
                'The Tax Bill has some errors that need to be fixed. Please address these to move forward.',
              type: 'error',
            });
          }
          return errors;
        }
      }
    },
    onSubmit: (formValues) => {
      setNavBlock(false);
      const result = _.cloneDeep(formValues);
      // Convert currency values to numbers
      if (result.taxableValue) {
        result.taxableValue = currencyToNumber(result.taxableValue, false);
      }
      if (result.totalTaxAmount) {
        result.totalTaxAmount = currencyToNumber(result.totalTaxAmount, false);
      }
      if (result.id) {
        setSubmitting(true);
        handleResponse(updateTaxBill(result.id, result));
      } else {
        setSubmitting(true);
        const parsedAssessmentNoticeId = assessmentNoticeId ? Number(assessmentNoticeId) : undefined;
        const request = { ...result, assessmentNoticeId: parsedAssessmentNoticeId };
        handleResponse(createTaxBill(request));
      }
    }
  });

  useEffect(() => {
    if (id) {
      window.scrollTo(0, 0);
      setFetchingTaxBill(true);
      getTaxBill(id)
        .then((d) => {
          const taxBillData = {
            ...d.data,
            paid: d.data.taxBillStatus === TaxBillStatus.Paid,
            datePaid: d.data.datePaid || null,
            totalTaxAmount: d.data.totalTaxAmount ? formatCurrency(d.data.totalTaxAmount, true) : null,
          };
          setInitialValues(taxBillData);
          setFetchingTaxBill(false);
        })
        .catch(() => {
          setFetchingTaxBill(false);
        });
    } else if (assessmentNoticeId) {
      setFetchingAssessmentNotice(true);
      getAssessmentNotice(assessmentNoticeId)
        .then((d) => {
          setInitialValues({
            ...newInit,
            assessmentNotice:d.data
          } );
          setFetchingAssessmentNotice(false);
        })
        .catch(() => {
          setFetchingAssessmentNotice(false);
        });
    }
  }, [id,assessmentNoticeId]);

  const renderPropertyInfoField = (
    label: string,
    value: string | undefined,
  ) => {
    return (
      <div className={style['info-field']}>
        <TooltipWrapper
          tooltipSection={TOOLTIP_SECTIONS.AccountInfo}
          tooltipKey={label}
        >
          <div>
            <div className={style['info-field-label']}>{label}</div>
            <div className={style['info-field-value']}>{value}</div>
          </div>
        </TooltipWrapper>
      </div>
    );
  };

  const getAssessorData = () => {
    const returnData = initialValues?.assessmentNotice?.return;
    if (!returnData) return null;

    return returnData.status === 'Filed' ?
      returnData.assessorAccountData :
      returnData.assessorAccount;
  };

  const assessorData = getAssessorData();
  // reset valid by click the close icon of error message box
  const [toastMessage, setToastMessage] = useState<IToastMessage>();
  const breadcrumb = [
    {
      value: 'Taxes',
      href: '/taxes',
    },
    { value: formik.values.taxBillStatus !== TaxBillStatus.Missing ? 'Edit Tax Bill' : (assessmentNoticeId)? 'Enter New Tax Bill' : 'Enter Tax Bill' },
  ];

  return (
    <>
      <div className={style['main-content']}>
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumb} />
        {/* Error message box,if there are errors message */}
        {toastMessage?.visible && (
          <div className={style['toast']}>
            <ToastMessage
              status={toastMessage}
              className='validate-error'
              visiableHandler={(value) =>
                setToastMessage({ ...toastMessage, visible: value })
              }
            />
          </div>
        )}
        <h2>
          {formik.values.taxBillStatus !== TaxBillStatus.Missing ? (
            <TooltipWrapper
              tooltipSection={TOOLTIP_SECTIONS.PageTitle}
              tooltipKey='Edit Tax Bill'
            >
              <span>Edit Tax Bill</span>
            </TooltipWrapper>
          ) : (
            <TooltipWrapper
              tooltipSection={TOOLTIP_SECTIONS.PageTitle}
              tooltipKey={assessmentNoticeId ? 'Enter New Tax Bill' : 'Enter Tax Bill'}
            >
              <span>{assessmentNoticeId ? 'Enter New Tax Bill' : 'Enter Tax Bill'}</span>
            </TooltipWrapper>
          )}
        </h2>
        <form className={style['main']} onSubmit={formik.handleSubmit}>
          {/* Account Header */}
          <div className={style['info-row']}>
            {renderPropertyInfoField(
              'Assessor Name',
              assessorData?.assessor?.name,
            )}
            {renderPropertyInfoField(
              'Assessor Account Number',
              assessorData?.number,
            )}
            {renderPropertyInfoField(
              'Property Number',
              assessorData?.property?.number,
            )}
            {renderPropertyInfoField(
              'Property Name',
              assessorData?.property?.name,
            )}
            {renderPropertyInfoField(
              'Property Address',
              (assessorData?.property?.address?.line1
                ? assessorData?.property?.address?.line1 + ', '
                : '') +
              (assessorData?.property?.address?.line2
                ? assessorData?.property?.address?.line2 + ', '
                : '') +
              (assessorData?.property?.address?.state?.abbreviation
                ? assessorData?.property?.address?.state?.abbreviation + ', '
                : '') +
              (assessorData?.property?.address?.postalCode || ''),
            )}
          </div>

          {/* Form sections */}
          <div className={classNames(style['section'])}>
            <div className={style['form']}>
              {/* Bill Status */}
              <fieldset className={style['form-section']}>
                <legend>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.Common}
                    tooltipKey='Bill Status'
                  >
                    <span>Bill Information</span>
                  </TooltipWrapper>
                </legend>
                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    Bill Status:
                  </div>
                  <div className={classNames(style['column'], style['text-right'])}>
                    <span className={classNames('badge-status', kebabCase(formik.values.taxBillStatus))}>
                      {formik.values.taxBillStatus}
                    </span>
                  </div>
                </div>

                <div className={style['row']}>
                  <div className={style['column']}>
                    Payment Deadline<span className={style['requiredMarker']}>*</span>:
                  </div>
                  <div className={style['column']}>
                    <FieldDate
                      primaryFormat='MM/DD/YYYY'
                      labelText=''
                      minDate={getFirstDateOfThisYear()}
                      maxDate={getLastDateOfThisYear()}
                      selectValue={formik.values.paymentDeadline}
                      onSelect={(value) =>
                        formik.setFieldValue(`paymentDeadline`, value)
                      }
                      error={
                        formik.errors &&
                        (formik.errors as FormikValues).paymentDeadline
                      }
                      required
                    />
                  </div>
                </div>

                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    Collector:
                  </div>
                  <div className={style['column']}>
                    <FieldInput
                      name='collector'
                      labelText=''
                      placeholder='Enter collector name'
                      value={formik.values?.collector ?? ''}
                      onChange={(newVal) => {
                        formik.setFieldValue(`collector`, newVal);
                      }}
                    />
                  </div>
                </div>

                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    Collector Account Number:
                  </div>
                  <div className={style['column']}>
                    <FieldInput
                      name='collectorAccountNumber'
                      labelText=''
                      placeholder='Enter account number'
                      value={formik.values?.collectorAccountNumber ?? ''}
                      onChange={(newVal) => {
                        formik.setFieldValue(`collectorAccountNumber`, newVal);
                      }}
                    />
                  </div>
                </div>
              </fieldset>
              <Divider className={style.divider} />
              {/* Assessment Values */}
              <fieldset className={style['form-section']}>
                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    Notice Status:
                  </div>
                  <div className={classNames(style['column'], style['text-right'])}>
                    <span className={classNames('badge-status', kebabCase(initialValues?.assessmentNotice?.noticeStatus))}>
                      {initialValues?.assessmentNotice?.noticeStatus}
                    </span>
                  </div>
                </div>
                <legend>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.Common}
                    tooltipKey='Assessment Values'
                  >
                    <span>Assessment Values</span>
                  </TooltipWrapper>
                </legend>
                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    Reported Assessed Value:
                  </div>
                  <div className={classNames(style['column'], style['text-right'])}>
                    {formatCurrency(initialValues?.assessmentNotice?.return?.totalAssessedValue)}
                  </div>
                </div>
                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    Notice Assessed Value:
                  </div>
                  <div className={classNames(style['column'], style['text-right'])}>
                    {formatCurrency(
                      initialValues?.assessmentNotice?.noticeAssessedValue
                    )}
                  </div>
                </div>
                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    Final Assessed Value:
                  </div>
                  <div className={classNames(style['column'], style['text-right'])}>
                    {formatCurrency(
                      (initialValues?.assessmentNotice?.noticeAssessedValue
                        && initialValues?.assessmentNotice?.noticeStatus === AssessmentNoticeStatus.Accepted) ?
                        initialValues?.assessmentNotice?.noticeAssessedValue : initialValues?.assessmentNotice?.finalAssessedValue
                    )}
                  </div>
                </div>


              </fieldset>
              <Divider className={style.divider} />
              {/* Tax Values */}
              <fieldset className={style['form-section']}>
                <legend>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.Common}
                    tooltipKey='Tax Values'
                  >
                    <span>Tax Values</span>
                  </TooltipWrapper>
                </legend>
                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    Taxable Value:
                  </div>
                  <div className={style['column']}>
                    <FieldInput
                      name='taxableValue'
                      classnames={style['text-right']}
                      labelText=''
                      placeholder='Enter amount'
                      value={formatCurrency(formik.values?.taxableValue, false)}
                      onChange={(newVal) => {
                        formik.setFieldValue(
                          `taxableValue`,
                          newVal ? formatCurrency(newVal, false) : null,
                        );
                      }}
                    />
                  </div>
                </div>
                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    Total Tax Amount:
                  </div>
                  <div className={style['column']}>
                    <FieldInput
                      name='totalTaxAmount'
                      classnames={style['text-right']}
                      labelText=''
                      placeholder='Enter amount'
                      value={formik.values?.totalTaxAmount}
                      onChange={(newVal) => {
                        const newValue = newVal.replace(/[$,\s]/g, '');
                        if (isNaN(Number(newValue))) {
                          return;
                        }
                        formik.setFieldValue(`totalTaxAmount`, newVal);
                      }}
                      onBlur={() => {
                        formik.values?.totalTaxAmount ? formik.setFieldValue(`totalTaxAmount`, formatCurrency(formik.values.totalTaxAmount, true)) : null;
                      }}
                    />
                  </div>
                </div>
              </fieldset>

              {/* Payment Information */}
              <fieldset className={style['form-section']}>
                <legend>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.Common}
                    tooltipKey='Payment Information'
                  >
                    <span>Payment Information</span>
                  </TooltipWrapper>
                </legend>
                <div className={classNames(style['row'], 'table-layout')}>
                  <div className={style['column']}>
                    <CustomCheckbox
                      label='Paid'
                      value={formik.values.paid}
                      onChange={(value) => {
                        formik.setFieldValue('paid', value);
                        if (!value) {
                          formik.setFieldValue('datePaid', null);
                          formik.setFieldValue('paymentDetails', '');
                        }
                      }}
                    />
                  </div>
                </div>

                {formik.values.paid && (
                  <>
                    <div className={style['row']}>
                      <div className={style['column']}>
                        Date Paid<span className={style['requiredMarker']}>*</span>:
                      </div>
                      <div className={style['column']}>
                        <FieldDate
                          primaryFormat='MM/DD/YYYY'
                          labelText=''
                          minDate={getFirstDateOfThisYear()}
                          maxDate={getLastDateOfThisYear()}
                          selectValue={formik.values.datePaid}
                          onSelect={(value) => {
                            if (`${value}`.indexOf('Invalid') >= 0 || !value) {
                              formik.setFieldValue('datePaid',
                                null,
                              );
                            } else {
                              formik.setFieldValue(`datePaid`, value)
                            }
                          }}
                          required
                          error={
                            formik.errors &&
                            (formik.errors as FormikValues).datePaid
                          }
                        />
                      </div>
                    </div>

                    <div className={classNames(style['row'], 'table-layout')}>
                      <div className={style['column']}>
                        Payment Details:
                      </div>
                      <div className={style['column']}>
                        <FieldTextarea
                          value={formik.values?.paymentDetails ?? ''}
                          onChange={(newVal) => formik.setFieldValue(`paymentDetails`, newVal)}
                          placeholder='Enter payment details'
                        />
                      </div>
                    </div>
                  </>
                )}
              </fieldset>
            </div>
          </div>

          <div className={style['footer']}>
            <div className={style['buttons']}>
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Save'
              >
                <button
                  type='submit'
                  className='primary'
                  onClick={() => {
                    setFinalStatus();
                    setIsSubmit(true);
                    setSubmitToast(true);
                  }}
                  disabled={submitting}
                >
                  Save
                  {submitting ? (
                    <LoaderIndicator
                      className='button-loading'
                      loading={true}
                    />
                  ) : null}
                </button>
              </TooltipWrapper>
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Back'
              >
                <button
                  className='button secondary'
                  type='button'
                  onClick={() => navigate(`/taxes?tab=${NoticesBillsTab.TaxBills}`)}
                  disabled={submitting}
                >
                  Back
                </button>
              </TooltipWrapper>
            </div>
          </div>
        </form>
      </div>
      <DiscardChangeConfirmModal
        initialData={initialValues}
        inputData={formik.values}
        navBlock={navBlock}
      />

      <Loader isOpen={(fetchingTaxBill || fetchingAssessmentNotice)} />
    </>
  );
}

export default TaxBillSetup; 